import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Check from '../../../../svg/Check'
import LayoutHeader from '../../../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { postDataAPI } from '../../../../utils/fetchData'
import { clearAlerts, setErrorAlert, setSuccessAlert } from '../../../../store/alert/alertSlice'

// Updated subscription plans matching backend configuration
const SUBSCRIPTION_PLANS = [
    { 
        label: "Lite", 
        value: "lite", 
        monthlyPrice: 25, 
        yearlyPrice: 250,
        type: 0, // Monthly
        tier: "lite",
        usageAllowance: 1500,
        features: ["1,500 usage/month", "All apps access", "Basic analytics", "Email support"]
    },
    { 
        label: "Core", 
        value: "core", 
        monthlyPrice: 50, 
        yearlyPrice: 500,
        type: 1, // Monthly
        tier: "core",
        usageAllowance: 4000,
        features: ["4,000 usage/month", "All apps access", "Advanced analytics", "Priority support", "20% efficiency bonus"]
    },
    { 
        label: "Max", 
        value: "max", 
        monthlyPrice: 100, 
        yearlyPrice: 1000,
        type: 2, // Monthly
        tier: "max",
        usageAllowance: 10000,
        features: ["10,000 usage/month", "All apps access", "Premium analytics", "White-label tools", "40% efficiency bonus", "Dedicated support"]
    }
];

export default function Subscription() {
    const token = localStorage.getItem('dizeeToken')
    const user = useSelector(state => state?.user?.userArticle)
    const [currentTier, setCurrentTier] = useState(user?.subscriptionTier || 'free')
    const [selectedPlan, setSelectedPlan] = useState(0)
    const [isYearly, setIsYearly] = useState(false)
    const [loading, setLoading] = useState(false)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    useEffect(() => {
        const fetchCurrentSubscription = async () => {
            try {
                const res = await postDataAPI('stripe/get_payment_history_auth', {}, token);
                if (res.data.data.response) {
                    const activePlan = res.data.data.response;
                    setCurrentTier(activePlan.subscriptionTier || 'free');
                    
                    // Set selected plan based on current subscription
                    const planIndex = SUBSCRIPTION_PLANS.findIndex(p => p.tier === activePlan.subscriptionTier);
                    if (planIndex !== -1) {
                        setSelectedPlan(planIndex);
                        setIsYearly(activePlan.isYearly || false);
                    }
                }
            } catch (error) {
                console.log('error', error)
                dispatch(setErrorAlert(error?.response?.data?.message || 'An error occurred'))
            }
        }
        fetchCurrentSubscription()
    }, [token, dispatch])

    const handleSubscriptionChange = async () => {
        dispatch(clearAlerts());
        setLoading(true);
        
        try {
            const selectedPlanConfig = SUBSCRIPTION_PLANS[selectedPlan];
            const planType = isYearly ? selectedPlan + 3 : selectedPlan; // Yearly plans are offset by +3
            
            if (selectedPlanConfig.tier === currentTier && !needsBillingCycleUpdate()) {
                dispatch(setErrorAlert('You are already on this plan with the same billing cycle'));
                setLoading(false);
                return;
            }

            const response = await postDataAPI('stripe/change_plan_checkout_session', { 
                plan: planType,
                isYearly: isYearly 
            }, token);
            
            if (response.data?.data?.url) {
                window.location.href = response.data.data.url;
            } else {
                dispatch(setErrorAlert('Failed to create checkout session'));
            }
        } catch (error) {
            dispatch(setErrorAlert(error?.response?.data?.message || 'Subscription update failed'));
        } finally {
            setLoading(false);
        }
    };

    const needsBillingCycleUpdate = () => {
        // Check if user wants to switch between monthly/yearly for same tier
        const selectedPlanConfig = SUBSCRIPTION_PLANS[selectedPlan];
        return selectedPlanConfig.tier === currentTier;
    };

    const getCurrentPrice = (plan) => {
        return isYearly ? plan.yearlyPrice : plan.monthlyPrice;
    };

    const getSavings = (plan) => {
        const monthlyCost = plan.monthlyPrice * 12;
        const savings = monthlyCost - plan.yearlyPrice;
        const percentage = Math.round((savings / monthlyCost) * 100);
        return { amount: savings, percentage };
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                <div className="px-4 my-[50px] flex w-full justify-between">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Subscription Plans</span>
                        <div className="flex gap-[20px] text-[12px]">
                        <button 
                                className={`text-white cursor-pointer hover:text-gray-400 transition-colors ${loading ? 'opacity-50' : ''}`}
                            onClick={handleSubscriptionChange}
                            disabled={loading}
                        >
                                {loading ? 'Processing...' : 'Update'}
                        </button>
                            <p onClick={() => navigate('/more/setting')} className='text-white cursor-pointer hover:text-gray-400 transition-colors'>Go back</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {/* Billing Cycle Toggle - Top */}
                        <div className="flex justify-end mb-4">
                            <div className="flex items-center gap-[2px] p-[3px] bg-[rgba(255,255,255,0.05)] rounded-[6px] w-fit">
                                <div
                                    onClick={() => setIsYearly(false)}
                                    className={`px-3 py-[6px] rounded-[4px] text-[10px] cursor-pointer transition-all duration-300 ${
                                        !isYearly 
                                            ? 'bg-white text-black font-semibold' 
                                            : 'bg-transparent text-[rgba(255,255,255,0.5)] font-normal'
                                    }`}
                                >
                                    Monthly
                                </div>
                                <div
                                    onClick={() => setIsYearly(true)}
                                    className={`px-3 py-[6px] rounded-[4px] text-[10px] cursor-pointer transition-all duration-300 ${
                                        isYearly 
                                            ? 'bg-white text-black font-semibold' 
                                            : 'bg-transparent text-[rgba(255,255,255,0.5)] font-normal'
                                    }`}
                                >
                                    Yearly
                                </div>
                            </div>
                        </div>

                        {/* Usage Info */}
                        <div className="mb-4 p-3 bg-[rgba(23,162,184,0.05)] border border-[rgba(23,162,184,0.2)] rounded-lg">
                            <p className="text-[#17A2B8] text-[10px] font-semibold mb-1">How Usage Works</p>
                            <p className="text-[rgba(255,255,255,0.7)] text-[10px] leading-[1.4]">
                                Usage is calculated based on actions: AI requests and music data consume more, while basic operations like adding songs consume very little.
                            </p>
                </div>

                    {/* Current Plan Display */}
                        <div className="mb-6 p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg hover:border-[rgba(255,255,255,0.2)] transition-all">
                        <div className="flex items-center justify-between mb-2">
                                <span className="text-white text-[12px] font-semibold">Current Plan</span>
                            <div className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wide ${
                                    currentTier === 'free' ? 'bg-[rgba(255,255,255,0.2)] text-white' : 
                                    currentTier === 'lite' ? 'bg-[#17A2B8] text-white' : 
                                    currentTier === 'core' ? 'bg-[#9932CC] text-white' : 
                                    'bg-[#FF9500] text-white'
                            }`}>
                                {currentTier === 'free' ? 'Free' : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                            </div>
                        </div>
                        {currentTier === 'free' && (
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px]">300 usage/month • Pay-as-you-go overage</p>
                        )}
                    </div>



                    {/* Subscription Plans */}
                        <div className="space-y-3">
                        {SUBSCRIPTION_PLANS.map((plan, index) => {
                            const price = getCurrentPrice(plan);
                            const savings = getSavings(plan);
                            const isSelected = selectedPlan === index;
                            const isCurrent = plan.tier === currentTier;
                            
                            return (
                                <div
                                    key={plan.value}
                                    onClick={() => setSelectedPlan(index)}
                                        className={`p-4 bg-[rgba(255,255,255,0.05)] border rounded-lg cursor-pointer transition-all hover:border-[#FF2300] ${
                                        isSelected 
                                                ? 'border-[#FF2300]' 
                                            : isCurrent
                                                    ? 'border-[#17A2B8]'
                                                    : 'border-[rgba(255,255,255,0.1)]'
                                    }`}
                                >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-white font-semibold text-[12px]">{plan.label}</h3>
                                                {isCurrent && (
                                                    <span className="text-[10px] px-2 py-1 bg-[#17A2B8] text-white rounded font-bold">
                                                        CURRENT
                                                    </span>
                                                )}
                                                {isSelected && <Check className="h-[12px] w-[18px] text-[#FF2300]" />}
                                        </div>
                                        <div className="text-right">
                                                <div className="text-white font-semibold text-[12px]">
                                                ${price}
                                                    <span className="text-[10px] font-normal text-[rgba(255,255,255,0.5)] ml-1">
                                                    /{isYearly ? 'year' : 'month'}
                                                </span>
                                            </div>
                                            {isYearly && (
                                                    <div className="text-[#17A2B8] text-[10px] font-medium">
                                                        Save ${savings.amount}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                                            {plan.usageAllowance.toLocaleString()} usage/month
                                    </div>
                                    
                                    <div className="space-y-1">
                                        {plan.features.map((feature, idx) => (
                                                <div key={idx} className="flex items-center text-[10px] text-[rgba(255,255,255,0.7)]">
                                                    <div className="w-1 h-1 bg-[#17A2B8] rounded-full mr-2"></div>
                                                {feature}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Free Plan Info */}
                        <div className="mt-6 p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                            <h4 className="text-white font-semibold text-[12px] mb-2">Free Plan</h4>
                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] mb-2">
                                300 usage/month • Pay only for what you use beyond the limit
                        </p>
                            <div className="text-[10px] text-[rgba(255,255,255,0.4)]">
                            Pay-as-you-go overage pricing
                        </div>
                    </div>


                    </div>
                </div>
            </div>
        </LayoutHeader>
    )
}
